/**
 * CDN Upload Service for AI Stylist
 * Handles file uploads to Picsart CDN with validation and error handling
 */

import { APP_UPLOAD_URL } from '@/constants';
import { getContext } from '@/miniappContext';

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_ERROR_MESSAGE = 'Failed to upload file to CDN';
const UPLOAD_TYPE = 'editing-temp';

// ============================================================================
// CONFIGURATION
// ============================================================================

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Validate file before upload
 * @throws Error if file is invalid
 */
const validateFile = (file: File | Blob): void => {
  if (!file) {
    throw new Error('File is required');
  }
  if (file.size === 0) {
    throw new Error('File is empty');
  }
};

// ============================================================================
// ERROR HANDLING
// ============================================================================

/**
 * Extract error message from response with fallbacks
 */
const extractErrorMessage = async (response: Response): Promise<string> => {
  try {
    const data = await response.json();
    return data.message || data.error || DEFAULT_ERROR_MESSAGE;
  } catch {
    try {
      const text = await response.text();
      return text || DEFAULT_ERROR_MESSAGE;
    } catch {
      return DEFAULT_ERROR_MESSAGE;
    }
  }
};

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Get file extension from MIME type
 */
const getExtensionFromMimeType = (mimeType: string): string => {
  const map: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
  };
  return map[mimeType] || 'png';
};

/**
 * Convert base64 data URL to Blob
 * @param base64DataUrl - Data URL string (e.g., data:image/png;base64,...)
 * @returns Blob object
 */
export const base64ToBlob = (base64DataUrl: string): Blob => {
  const parts = base64DataUrl.split(',');
  const mimeMatch = parts[0].match(/:(.*?);/);
  let mimeType = mimeMatch ? mimeMatch[1] : 'image/png';

  if (mimeType === 'image/svg xml') {
    mimeType = 'image/svg+xml';
  }

  const base64Data = parts[1];
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

/**
 * Create FormData for CDN upload
 */
const createUploadFormData = (file: File): FormData => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', UPLOAD_TYPE);
  return formData;
};

// ============================================================================
// UPLOAD FUNCTIONS
// ============================================================================

/**
 * Upload blob directly to CDN (optimized path)
 * @param blob - Blob to upload
 * @returns CDN URL of uploaded file
 */
export const uploadBlobToCdn = async (blob: Blob): Promise<string> => {
  const ctx = getContext();
  const authenticatedFetch = ctx.utils.api.fetch;

  const timestamp = Date.now();
  const extension = getExtensionFromMimeType(blob.type);
  const filename = `wardrobe-upload-${timestamp}.${extension}`;
  const file = new File([blob], filename, { type: blob.type });

  validateFile(file);

  const formData = createUploadFormData(file);
  const uploadUrl = `${APP_UPLOAD_URL}/v2/files`;

  const response = await authenticatedFetch(uploadUrl, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response);
    console.error(`[CDNService] CDN upload failed:`, {
      status: response.status,
      statusText: response.statusText,
      error: errorMessage,
      blobSize: blob.size,
      blobType: blob.type,
    });
    throw new Error(`CDN upload failed (${response.status}): ${errorMessage}`);
  }

  const data = await response.json();
  const cdnUrl = data.response?.url || data.url;

  if (!cdnUrl) {
    throw new Error('No URL returned from CDN upload');
  }

  return cdnUrl;
};

/**
 * Upload base64 image to CDN
 * @param base64DataUrl - Base64 data URL string
 * @returns CDN URL of uploaded file
 */
export const uploadToCdn = async (base64DataUrl: string): Promise<string> => {
  const ctx = getContext();
  const authenticatedFetch = ctx.utils.api.fetch;

  if (!base64DataUrl || base64DataUrl === 'data:,' || base64DataUrl.length < 100) {
    throw new Error('Data URI is empty or too small to be a valid image');
  }

  const blob = base64ToBlob(base64DataUrl);
  validateFile(blob);

  const timestamp = Date.now();
  const extension = getExtensionFromMimeType(blob.type);
  const filename = `wardrobe-upload-${timestamp}.${extension}`;
  const file = new File([blob], filename, { type: blob.type });

  const formData = createUploadFormData(file);
  const uploadUrl = `${APP_UPLOAD_URL}/v2/files`;

  const response = await authenticatedFetch(uploadUrl, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response);
    console.error(`[CDNService] CDN upload failed:`, {
      status: response.status,
      statusText: response.statusText,
      error: errorMessage,
      blobSize: blob.size,
    });
    throw new Error(`CDN upload failed (${response.status}): ${errorMessage}`);
  }

  const data = await response.json();
  const cdnUrl = data.response?.url || data.url;

  if (!cdnUrl) {
    throw new Error('No URL returned from CDN upload');
  }

  return cdnUrl;
};

/**
 * Check if a URL is a localhost URL
 */
export const isLocalUrl = (url: string): boolean => {
  return url.includes('localhost') || url.includes('127.0.0.1');
};

/**
 * Upload from localhost URL to CDN
 * @param url - Local URL to fetch and upload
 * @returns CDN URL of uploaded file
 */
export const uploadLocalUrlToCdn = async (url: string): Promise<string> => {
  const response = await fetch(url);

  if (!response.ok) {
    console.error(`[CDNService] Failed to fetch local image:`, {
      status: response.status,
      url,
    });
    throw new Error(`Failed to fetch local image: ${response.status}`);
  }

  const blob = await response.blob();
  const cdnUrl = await uploadBlobToCdn(blob);

  return cdnUrl;
};
